import React, { useEffect, useState } from 'react';
import { DndContext } from '@dnd-kit/core';
import { CardBody, CardTitle, Col } from 'reactstrap';
import { MainContainer } from '../Components/MainContainer';
import { TCohort, Thistorico_avancado } from '../Types/CohortType';
import { Api } from '../Services/CohortServices';
import '../Styles/CohortCardsStyle.css'
import { useNavigate } from 'react-router-dom';
import { DroppableArea } from '../Components/CreateCohort/DragAndDropsComponent';
import { SelecioneCarteiraOuNao } from '../Components/CreateCohort/ClienteCarteiraComponent';
import { Attribute, getInitialAttributes, ListAtributesComponent, ValoresHistoricos } from '../Components/CreateCohort/ListAtributesComponent';
import { ListAtributesSelectedComponent } from '../Components/CreateCohort/ListAtributesSelectedComponent';
import { ToasterAdviser } from '../Components/ToasterComponent';

// Componente principal
export const BuildView = ({ cohort }: { cohort?: TCohort }) => {
  const [availableAttributes, setAvailableAttributes] = useState(getInitialAttributes(cohort?.carteira === true ? 'true' : 'false'));
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedAttributes, setSelectedAttributes] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [nextTag, setNextTag] = useState<number | null>(null);
  const [selectedWallet, setSelectedWallet] = useState<string>('');
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [DateConsulta, setDateConsulta] = useState<[Date | null, Date | null]>([null, null]);
  const [startDate, endDate] = dateRange;
  const [startDateConsulta, endDateConsulta] = DateConsulta;
  const navigate = useNavigate();

  function flattenAttributes(attributes: Attribute[]): Attribute[] {
    return attributes.flatMap(attr => {
      if (Array.isArray(attr.type)) {
        return attr.type;
      }
      return attr;
    });
  }

  useEffect(() => {
    const controller = new AbortController();
    if (cohort && selectedWallet !== '') {

      const selectedBase = Object.entries(cohort)
        .filter(([key, value]) => value !== undefined && value !== null && typeof value !== 'object')
        .map(([key, value]) => {
          const attribute = getInitialAttributes(selectedWallet).find(attr =>
            attr.name
              .normalize('NFD')
              .replace(/[\u0300-\u036f]/g, '')
              .toLowerCase()
              .replace(/\s/g, '_')
            === key
          );
          if (!attribute) return null;

          const conditionKey = `condition_${key}`;
          const condition = (cohort as Record<string, any>)[conditionKey] || (attribute.type === 'number' || attribute.type === 'date' ? 'greater' : undefined);

          return { ...attribute, value, condition };
        })
        .filter(attr => attr !== null) as any[];


      const allAttributes = flattenAttributes(getInitialAttributes(selectedWallet));
      const histAvancadoAttrs = cohort.hist_avancado
        ? Object.entries(cohort.hist_avancado)
          .filter(([key, value]) => value !== undefined && value !== null)
          .map(([key, value]) => {
            const attribute = allAttributes.find(attr =>
              attr.name
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .toLowerCase()
                .replace(/\s/g, '_') === key
            );


            if ((key === 'data_cadastro' || key === 'data_consulta') && typeof value === 'string') {
              const [startStr, endStr] = value.split(' - ');
              const start = startStr ? new Date(startStr) : null;
              const end = endStr ? new Date(endStr) : null;

              if (key === 'data_cadastro') {
                setDateRange([start, end]);
              } else if (key === 'data_consulta') {
                setDateConsulta([start, end]);
              }
            }

            return attribute ? { ...attribute, value, condition: 'greater' } : null;
          })
          .filter(attr => attr !== null) as any[]
        : [];

      const selected = [...selectedBase, ...histAvancadoAttrs];
      setSelectedAttributes(selected);
      setAvailableAttributes(prev =>
        prev.filter(attr => !selected.some(sel => sel.id === attr.id))
      );
    }
    return () => {
      controller.abort();
    };
    // eslint-disable-next-line
  }, [cohort, selectedWallet]);

  useEffect(() => {
    if (cohort && selectedWallet === '') {
      const carteiraValue = cohort.carteira === true ? 'true' : 'false';
      setSelectedWallet(carteiraValue ?? '');
    }
  }, [cohort, selectedWallet]);

  const validateRequiredFields = (): string[] => {
    const missingFields: string[] = [];
    const hasHistoricoAvancado = selectedAttributes.some(attr => attr.id === '19');

    if (hasHistoricoAvancado) {
      ValoresHistoricos.forEach((campo) => {
        if (campo.required) {
          const attr = selectedAttributes.find((a) => a.name === campo.name);

          const isRange = campo.type === 'range';
          const isFase = campo.type === 'fase';

          if (isRange) {
            if (campo.name === 'Data de Cadastro' && (!startDate || !endDate)) {
              missingFields.push(campo.name);
            } else if (campo.name === 'Data de Consulta' && (!startDateConsulta || !endDateConsulta)) {
              missingFields.push(campo.name);
            }
          } else if (isFase) {
            if (!attr || !attr.value) {
              missingFields.push(campo.name);
            }
          } else {
            if (!attr || attr.value === undefined || attr.value === '') {
              missingFields.push(campo.name);
            }
          }
        }
      });
    }

    return missingFields;
  };

  const handleSave = async () => {
    const missingFields = validateRequiredFields();

    if (missingFields.length > 0) {
      setError(`Por favor, preencha os campos obrigatórios:\n- ${missingFields.join('\n- ')}`);
      return;
    }

    const historico: Partial<Thistorico_avancado> = {};
    const cohortData: TCohort = selectedAttributes.reduce((acc: TCohort, attr) => {
      switch (attr.name) {
        case 'Liberado consulta':
          acc.liberado_consulta = attr.value === 'true';
          break;
        case 'Possui saldo':
          acc.possui_saldo = attr.value === 'true';
          break;
        case 'cadastro proposta':
          acc.cadastro_proposta = attr.value || undefined;
          acc.condition_cadastro_proposta = attr.condition;
          break;
        case 'Última consulta':
          acc.ultima_consulta = attr.value || undefined;
          acc.condition_ultima_consulta = attr.condition;
          break;
        case 'Valor saldo':
          acc.valor_saldo = attr.value ? parseFloat(attr.value) : undefined;
          acc.condition_valor_saldo = attr.condition;
          break;
        case 'Proposta cadastrada':
          acc.proposta_cadastrada = attr.value === 'true';
          break;
        case 'Reposição de saldo':
          acc.reposicao_saldo = attr.value === 'true';
          break;
        case 'Saque concorrente':
          acc.saque_concorrente = attr.value === 'true';
          break;
        case 'Data última compra':
          acc.data_ultima_compra = attr.value || undefined;
          acc.condition_data_ultima_compra = attr.condition;
          break;
        case 'Quantidade de compras':
          acc.qtd_compras = attr.value ? parseInt(attr.value, 10) : undefined;
          acc.condition_qtd_compras = attr.condition;
          break;
        case 'Intervalo entre compras':
          acc.intervalo_entre_compras = attr.value ? parseInt(attr.value, 10) : undefined;
          acc.condition_intervalo_entre_compras = attr.condition;
          break;
        case 'Mes aniversário':
          acc.data_aniversario = attr.value || undefined;
          break;
        case 'Black list':
          acc.black_list = attr.value === 'true';
          break;
        case 'Projeção de liberação de saldo':
          acc.projecao_liberacao_saldo = attr.value || undefined;
          acc.condition_projecao_liberacao_saldo = attr.condition;
          break;
        case 'Autorizado Qi':
          acc.autorizado_qi = attr.value === 'true';
          break;
        case 'Autorizado Facta':
          acc.autorizado_facta = attr.value === 'true';
          break;
        case 'Autorizado Uy3':
          acc.autorizado_uy3 = attr.value === 'true';
          break;
        case 'Autorizado Bmp':
          acc.autorizado_bmp = attr.value === 'true';
          break;
        case 'Data Cadastro':
          if (startDate && endDate) {
            historico.data_cadastro = `${startDate.toISOString()} - ${endDate.toISOString()}`;
          }
          break;
        case 'Data Consulta':
          if (startDateConsulta && endDateConsulta) {
            historico.data_consulta = `${startDateConsulta.toISOString()} - ${endDateConsulta.toISOString()}`;
          }
          break;
        case 'Fase':
          historico.fase = attr.value || undefined;
          break;
        case 'Agente':
          acc.agente = attr.value || undefined;
          break;
        default:
          break;
      }
      return acc;
    }, {} as TCohort);

    const cohortTag = cohort?.tag ?? nextTag;
    if (cohortTag) {
      cohortData.tag = cohortTag;
    }

    if (selectedWallet) {
      cohortData.carteira = selectedWallet === 'true' ? true : false;
    }
    if (Object.keys(historico).length > 0) {
      cohortData.hist_avancado = historico as Thistorico_avancado;
    }

    try {
      setIsLoading(true);
      if (cohort) {
        await Api.put(`/cohorts/${cohort.id}`, cohortData);
        setSuccess('Cohort atualizado com sucesso!');
        setTimeout(() => {
          setSuccess(null);
          setError(null);
          navigate('/dashboard')
        }, 3000);
      } else {
        await Api.post('/cohorts', cohortData);
        setSuccess('Cohort criado com sucesso!');
        setTimeout(() => {
          setSuccess(null);
          setError(null);
          navigate('/dashboard')
        }, 3000);
      }
    } catch (error) {
      console.error('Erro ao salvar:', error);
      setError('Erro ao salvar o cohort. Tente novamente mais tarde.');
      setTimeout(() => {
        setSuccess(null);
        setError(null);
        navigate('/dashboard')
      }, 3000);
    } finally {
      setIsLoading(false);

    }
  };

  const handleDragEnd = ({ over, active }: { over: any; active: any }) => {
    if (!over) return;

    const draggedItem = availableAttributes.find((attr) => attr.id === active.id);
    if (!draggedItem || over.id !== 'selected') return;

    if (Array.isArray(draggedItem.type)) {
      const jaExpandido = draggedItem.type.every((subAttr) =>
        selectedAttributes.some((attr) => attr.id === subAttr.id)
      );
      if (jaExpandido) return;

      const subItems = draggedItem.type.map((subAttr: any) => ({
        ...subAttr,
        value: '',
        condition: 'greater',
        parentId: draggedItem.id,
      }));

      setSelectedAttributes((prev) => [...prev, ...subItems]);
    } else {
      setAvailableAttributes((prev) => prev.filter((attr) => attr.id !== active.id));
      setSelectedAttributes((prev) => [
        ...prev,
        {
          ...draggedItem,
          value: '',
          condition: draggedItem.type === 'number' || draggedItem.type === 'date' ? 'greater' : '',
        },
      ]);
    }
  };

  const handleInputChange = (id: string, key: string, value: any) => {
    setSelectedAttributes((prev) =>
      prev.map((attr) => (attr.id === id ? { ...attr, [key]: value } : attr))
    );
  };

  const RemoveDragableItem = (id: string) => {
    const item = selectedAttributes.find((attr) => attr.id === id);
    if (item) {
      setSelectedAttributes((prev) => prev.filter((attr) => attr.id !== id));
      setAvailableAttributes((prev) => [...prev, item]);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await Api.get('/cohorts');
      const tags = response.data.cohorts.map((cohort: any) => cohort.tag).filter((tag: any) => typeof tag === 'number');

      // Ordena as tags e encontra a menor disponível
      tags.sort((a: number, b: number) => a - b);
      let availableTag = 1;

      for (const tag of tags) {
        if (tag !== availableTag) break;
        availableTag++;
      }

      setNextTag(availableTag);
    } catch (error) {
      console.error('Erro ao buscar tags dos cohorts:', error);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    fetchTags();
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const initialAttrs = getInitialAttributes(selectedWallet);
    setAvailableAttributes(initialAttrs);
  }, [selectedWallet]);

  //View principal 
  return (
    (selectedWallet === '') ? SelecioneCarteiraOuNao({ setSelectedWallet, selectedWallet }) : (
      <DndContext onDragEnd={handleDragEnd}>
        <MainContainer>
          <>
            <div className="main-content">
              <div className="mt-1">
                <Col md={12}>
                  {!cohort ?
                    <div style={{ fontSize: '2.25rem ', fontWeight: '900 ', color: '#000' }}>Criar Cohort</div> :
                    <div style={{ fontSize: '2.25rem ', fontWeight: '900 ', color: '#000' }}>Edição de Cohort</div>
                  }
                  <div className="d-flex gap-2 mt-4">
                    {/* Coluna de atributos disponíveis */}

                    <div style={{ flex: 1 }}>
                      <div style={{ padding: '0.6rem' }}>
                        <CardBody>
                          <CardTitle style={{ fontSize: '1.25rem', fontWeight: '700', color: '#000' }}>
                            Atributos Disponíveis
                          </CardTitle>
                          <div className='w-100 p-1 mt-2'>
                            <ListAtributesComponent
                              availableAttributes={availableAttributes}
                              selectedWallet={selectedWallet}
                              startDate={startDate}
                              endDate={endDate}
                              startDateConsulta={startDateConsulta}
                              endDateConsulta={endDateConsulta}
                            />
                          </div>
                        </CardBody>
                      </div>
                    </div>
                    {/* Coluna de montagem da pesquisa */}
                    <div className='w-75'>
                      <div style={{ padding: '0.6rem' }}>
                        <CardBody>
                          <div className='d-flex justify-content-between'>
                            <CardTitle style={{ fontSize: '1.25rem ', fontWeight: '700 ', color: '#000' }}>Cohort Builder</CardTitle>
                          </div>
                          <DroppableArea id="selected" onSave={handleSave} isLoading={isLoading} selectedAttributesLength={selectedAttributes.length}>
                            <ListAtributesSelectedComponent
                              selectedAttributes={selectedAttributes}
                              setDateRange={setDateRange}
                              setDateConsulta={setDateConsulta}
                              startDate={startDate}
                              endDate={endDate}
                              startDateConsulta={startDateConsulta}
                              endDateConsulta={endDateConsulta}
                              handleInputChange={handleInputChange}
                              RemoveDragableItem={RemoveDragableItem}
                            />
                            {(success || error) && (
                              <ToasterAdviser
                                type={success ? "success" : "error"}
                                message={success || error}
                                icon={success ? "success" : "error"}
                              />
                            )}
                          </DroppableArea>
                        </CardBody>
                      </div>
                    </div>
                  </div>
                </Col>
              </div>
            </div>
          </>
        </MainContainer>
      </DndContext>)
  );
};

